import { isAxiosError } from 'axios';

const FALLBACK_MESSAGE = 'Something went wrong';

/**
 * What every call made through the app's axios instance rejects with.
 *
 * The response interceptor used to reject the raw response body, so any API
 * rejection that went uncaught reached Sentry as "Object captured as exception
 * with keys: error, message, statusCode": no stack, no usable title, and one
 * group for unrelated failures. A real Error fixes all three while keeping
 * `.message` exactly where callers already read it.
 */
export class ApiError extends Error {
  /** HTTP status, when the request got a response at all. */
  readonly status?: number;

  /**
   * The response body, untouched. Callers that need more than the message read
   * it from here — a `responseType: 'blob'` download carries its error as a
   * Blob rather than JSON, for instance.
   */
  readonly payload: unknown;

  constructor(
    message: string,
    options: { status?: number; payload?: unknown; cause?: unknown },
  ) {
    super(message, { cause: options.cause });
    this.name = 'ApiError';
    this.status = options.status;
    this.payload = options.payload;
  }
}

/**
 * NestJS answers with `{ message }` — a string, or an array of strings when
 * class-validator rejects a payload. Anything else (a Blob body, an empty
 * response, a request that never reached the server) has no message to show.
 */
function readPayloadMessage(payload: unknown): string | null {
  if (
    typeof payload !== 'object' ||
    payload === null ||
    !('message' in payload)
  ) {
    return null;
  }

  const { message } = payload;

  if (typeof message === 'string' && message.length > 0) {
    return message;
  }

  if (Array.isArray(message)) {
    const [first] = message;

    return typeof first === 'string' && first.length > 0 ? first : null;
  }

  return null;
}

/**
 * The message the backend actually sent, or `null` when it sent none: a
 * request that never got a response, an empty body, a Blob download.
 *
 * Callers that have better wording of their own for that case read this rather
 * than `toApiError`, whose generic fallback would otherwise overwrite it.
 */
export function readApiErrorMessage(error: unknown): string | null {
  if (error instanceof ApiError) {
    return readPayloadMessage(error.payload);
  }

  return readPayloadMessage(
    isAxiosError(error) ? error.response?.data : undefined,
  );
}

/**
 * Wraps whatever axios rejected with into an `ApiError`. The message is the
 * one the backend sent, so views keep translating the same key they always
 * did; the fallback covers the requests that never got a response (network
 * down, timeout, CORS), where the underlying AxiosError is kept as `cause` and
 * carries the actual failure to Sentry.
 */
export function toApiError(error: unknown): ApiError {
  if (error instanceof ApiError) {
    return error;
  }

  const response = isAxiosError(error) ? error.response : undefined;
  const payload = response?.data;

  return new ApiError(readPayloadMessage(payload) ?? FALLBACK_MESSAGE, {
    status: response?.status,
    payload,
    cause: error,
  });
}
